import { z } from 'zod'
import { ArchivedFilterSchema } from './archive'

export const ResponseSuccessSchema = z.object({
  type: z.literal('success').meta({
    id: 'responseSuccessType',
    title: 'Success Response',
    description: 'Standard success response for API calls',
  }),
})
export type ResponseSuccess = z.infer<typeof ResponseSuccessSchema>

export const ErrorTypeSchema = z.enum([
  'Server',
  'InvalidRequest',
  'NotAuthorized',
  'Deactivated',
  'Network',
  'NotFound',
  'Duplicate',
  'HighLoad',
  'RequestTimeout',
  'Conflict',
])
export type ErrorType = z.infer<typeof ErrorTypeSchema>

export const ResponseErrorSchema = z
  .object({
    type: z.literal('error'),
    error: z.string(),
    message: z.string().optional(),
    retry: z.boolean().optional(),
  })
  .meta({ id: 'responseError', title: 'Error Response', description: 'Standard error response for API calls' })
export type ResponseError = z.infer<typeof ResponseErrorSchema>

export type ResponseOutput = ResponseSuccess | ResponseError

/** `version` is the only request control an endpoint can expose (`apiRequest`'s `exposeVersion`). */
const ApiVersionSchema = z
  .number()
  .int()
  .positive()
  .optional()
  .meta({ id: 'apiVersion', description: 'API version number.' })

const ApiRequestBaseSchema = z.object({
  version: ApiVersionSchema.meta({ audience: 'none' }),
  clearLocalCache: z.boolean().optional().meta({
    id: 'apiClearLocalCache',
    description: 'If true, clears local cache before processing the request.',
    audience: 'none',
  }),
  /** skips reading from the distributed cache */
  skipDistributedCache: z.boolean().optional().meta({
    id: 'apiSkipDistributedCache',
    description: 'If true, skips reading from the distributed cache.',
    audience: 'none',
  }),
  fakeError: z.string().optional().meta({
    id: 'apiFakeError',
    description: 'If set, forces the API to return the specified error.',
    audience: 'none',
  }),
  echo: z
    .boolean()
    .optional()
    .meta({ id: 'apiEcho', description: 'If true, echoes the request in the response.', audience: 'none' }),
})

export const EmptyApiRequestSchema = ApiRequestBaseSchema
export type EmptyApiRequest = z.infer<typeof EmptyApiRequestSchema>

export type ApiRequestBase = z.infer<typeof ApiRequestBaseSchema>
/** @deprecated use zod schema */
export type ApiRequest<T> = T & ApiRequestBase

export const RemoveArchivableSchema = z.object({
  /** @defaultValue true - set to "false" to hard delete database record */
  archive: z.boolean().optional().meta({
    id: 'archive',
    description: 'Set to false to hard delete database record',
  }),
})
export type RemoveArchivable = z.infer<typeof RemoveArchivableSchema>

export const ShowArchivedSchema = z.object({
  showArchived: ArchivedFilterSchema.optional().meta({
    id: 'showArchived',
    description: 'If "all", include archived records. If true, include only archived records.',
  }),
})
/** @see filterArchived() */
export type ShowArchived = z.infer<typeof ShowArchivedSchema>

// Presigned upload URLs are write credentials, so read endpoints return them only to callers who may edit the entity.
// Omitting the flag still returns them for now so shipped apps keep working, until omitted comes to mean false.
export const IncludeUploadUrlsSchema = z.object({
  includeUploadUrls: z.boolean().optional().meta({
    id: 'includeUploadUrls',
    description:
      'Include presigned upload URLs for the images you may edit. Pass true when you are about to upload. For now an omitted flag still returns them, but that is deprecated: it will soon mean false.',
  }),
})

type ApiResponsesOutput<T extends Record<string, z.ZodTypeAny>> = [
  z.ZodObject<T & typeof ResponseSuccessSchema.shape>,
  z.ZodDiscriminatedUnion<[z.ZodObject<T & typeof ResponseSuccessSchema.shape>, typeof ResponseErrorSchema]>,
]

export function apiResponses<T extends Record<string, z.ZodTypeAny>>(schema: T): ApiResponsesOutput<T> {
  const successSchema = z.object({
    ...ResponseSuccessSchema.shape,
    ...schema,
  })
  const responseOutputSchema = z.discriminatedUnion('type', [successSchema, ResponseErrorSchema])
  return [successSchema, responseOutputSchema]
}

type ApiRequestOptions = {
  exposeVersion?: boolean
}

export function apiRequest<T extends z.ZodRawShape>(
  schema: T,
  { exposeVersion = false }: ApiRequestOptions = {}
): z.ZodObject<T & typeof ApiRequestBaseSchema.shape> {
  return z.object({
    ...ApiRequestBaseSchema.shape,
    ...(exposeVersion && { version: ApiVersionSchema }),
    ...schema,
  })
}

export function emptyApiRequest(): z.ZodObject<typeof EmptyApiRequestSchema.shape> {
  return z.object(EmptyApiRequestSchema.shape)
}
